/*
 * SofaStyle – faster variant switching on the product page.
 * 1. Preloads each variant's featured image (idle time + on hover/touch).
 * 2. Swaps the gallery / sticky bar image immediately when a variant is chosen.
 * 3. Prefetches the variant page HTML on hover/touch and hands it to the
 *    theme's own variant-picker fetch, so the update often arrives instantly.
 * The theme's normal update still runs afterwards and stays the source of truth.
 */
(function () {
  'use strict';
  if (window.__ssVariantSpeed) return;
  window.__ssVariantSpeed = true;

  var configs = document.querySelectorAll('script[data-ss-variant-speed]');
  if (!configs.length) return;

  var IMAGES = {}; // variantId -> image base url (//host/cdn/shop/files/x.png?v=123)
  configs.forEach(function (el) {
    try {
      var data = JSON.parse(el.textContent || '{}');
      Object.keys(data).forEach(function (k) { if (data[k]) IMAGES[k] = data[k]; });
    } catch (e) {}
  });

  var conn = navigator.connection || {};
  var slowNet = conn.saveData || /(^|-)2g|3g/.test(conn.effectiveType || '');

  function pickers() {
    return Array.prototype.filter.call(document.querySelectorAll('variant-picker'), function (vp) {
      return vp.dataset.templateProductMatch === 'true' && !vp.closest('product-card, quick-add-dialog');
    });
  }

  function currentVariantId() {
    var vp = pickers()[0];
    if (!vp) return null;
    var checked = vp.querySelector('fieldset input:checked, select option[selected]');
    if (checked && checked.dataset.variantId) return checked.dataset.variantId;
    return new URL(location.href).searchParams.get('variant');
  }

  var shownBase = IMAGES[currentVariantId()] || null;

  /* Images currently showing the selected variant's picture */
  function templateImages(base) {
    if (!base) return [];
    return Array.prototype.filter.call(document.querySelectorAll('img[srcset], img[src]'), function (img) {
      var s = (img.getAttribute('srcset') || '') + ' ' + (img.getAttribute('src') || '');
      return s.indexOf(base) !== -1;
    });
  }

  function swap(str, from, to) {
    return str ? str.split(from).join(to) : str;
  }

  /* ---------- 1. Image preloading ---------- */
  var preloaded = {};
  function preloadVariantImage(variantId, priority) {
    var base = IMAGES[variantId];
    if (!base || preloaded[base] || !shownBase || base === shownBase) return;
    var templates = templateImages(shownBase);
    if (!templates.length) return;
    preloaded[base] = true;
    templates.forEach(function (img) {
      var pre = new Image();
      if (priority) pre.fetchPriority = priority;
      pre.decoding = 'async';
      if (img.sizes) pre.sizes = img.sizes;
      var srcset = img.getAttribute('srcset');
      if (srcset) pre.srcset = swap(srcset, shownBase, base);
      pre.src = swap(img.getAttribute('src') || '', shownBase, base);
    });
  }

  function idlePreloadAll() {
    if (slowNet) return;
    var ids = Object.keys(IMAGES);
    var i = 0;
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 200); };
    (function next() {
      if (i >= ids.length) return;
      idle(function () {
        preloadVariantImage(ids[i++], 'low');
        setTimeout(next, 120);
      });
    })();
  }

  /* ---------- 2. Instant image swap ---------- */
  function showVariantImage(variantId) {
    var base = IMAGES[variantId];
    if (!base || !shownBase || base === shownBase) return;
    templateImages(shownBase).forEach(function (img) {
      var srcset = img.getAttribute('srcset');
      if (srcset) img.setAttribute('srcset', swap(srcset, shownBase, base));
      var src = img.getAttribute('src');
      if (src) img.setAttribute('src', swap(src, shownBase, base));
    });
    shownBase = base;
  }

  /* ---------- 3. HTML prefetch handed to the theme's fetch ---------- */
  var CACHE = new Map(); // url -> { time, promise<Response> }
  var TTL = 30000;
  var nativeFetch = window.fetch.bind(window);

  function requestUrlFor(vp, input) {
    if (new URL(location.href).searchParams.get('view')) return null;
    var productUrl = input.dataset.connectedProductUrl || vp.dataset.productUrl;
    if (!productUrl || (input.dataset.connectedProductUrl && productUrl.split('?')[0] !== (vp.dataset.productUrl || '').split('?')[0])) return null;
    var fieldsetIndex = input.dataset.fieldsetIndex;
    var selected = Array.prototype.slice.call(vp.querySelectorAll('select option[selected], fieldset input:checked'));
    var ids = selected.map(function (el) {
      var sameFieldset = el instanceof HTMLInputElement && el.dataset.fieldsetIndex === fieldsetIndex;
      return sameFieldset ? input.dataset.optionValueId : el.dataset.optionValueId;
    });
    if (ids.some(function (id) { return !id; })) return null;
    return productUrl + '?option_values=' + ids.join(',');
  }

  function prefetch(url) {
    var hit = CACHE.get(url);
    if (hit && Date.now() - hit.time < TTL) return;
    var promise = nativeFetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('prefetch failed');
      return r;
    });
    promise.catch(function () { CACHE.delete(url); });
    CACHE.set(url, { time: Date.now(), promise: promise });
    if (CACHE.size > 20) CACHE.delete(CACHE.keys().next().value);
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : null;
    if (url && url.indexOf('option_values=') !== -1) {
      var hit = CACHE.get(url);
      CACHE.delete(url);
      if (hit && Date.now() - hit.time < TTL) {
        var signal = init && init.signal;
        var served = hit.promise.then(function (r) { return r.clone(); });
        if (!signal) return served;
        return new Promise(function (resolve, reject) {
          var abort = function () { reject(new DOMException('The operation was aborted.', 'AbortError')); };
          if (signal.aborted) return abort();
          signal.addEventListener('abort', abort, { once: true });
          served.then(resolve, reject);
        });
      }
    }
    return nativeFetch.apply(window, arguments);
  };

  /* ---------- Wiring ---------- */
  function inputFromEvent(e) {
    var t = e.target;
    if (!(t instanceof Element)) return null;
    var label = t.closest('label');
    var input = t instanceof HTMLInputElement ? t : label && label.querySelector('input[type="radio"]');
    if (!input || input.checked) return null;
    var vp = input.closest('variant-picker');
    if (!vp || pickers().indexOf(vp) === -1) return null;
    return { input: input, vp: vp };
  }

  var lastIntent = null;
  function onIntent(e) {
    var hit = inputFromEvent(e);
    if (!hit || hit.input === lastIntent) return;
    lastIntent = hit.input;
    if (hit.input.dataset.variantId) preloadVariantImage(hit.input.dataset.variantId, 'high');
    var url = requestUrlFor(hit.vp, hit.input);
    if (url) prefetch(url);
  }

  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio') return;
    var vp = t.closest('variant-picker');
    if (!vp || pickers().indexOf(vp) === -1) return;
    lastIntent = null;
    if (t.dataset.variantId) showVariantImage(t.dataset.variantId);
  }, true);

  // After the theme re-renders the gallery, resync what is shown.
  document.addEventListener('variant:update', function () {
    setTimeout(function () { shownBase = IMAGES[currentVariantId()] || shownBase; }, 0);
  });

  if (document.readyState === 'complete') {
    setTimeout(idlePreloadAll, 1500);
  } else {
    window.addEventListener('load', function () { setTimeout(idlePreloadAll, 1500); });
  }
})();
